/**
 * Pure URL-hash ⇆ Route grammar for the router (OMON-202).
 *
 * Deliberately import-free (only its own Route type) so the routing grammar is
 * unit-testable in Node — router.ts itself transitively pulls in the chart
 * stack (chartjs-plugin-zoom → hammerjs) which needs a DOM. router.ts imports
 * parse/build from here and owns the side-effecting apply/navigate logic.
 *
 * The tab/admin string unions are inlined (identical to state.DetailActiveTab
 * and shell.AdminPanel) so this module has zero dependencies; the app types are
 * the same string literals, so values flow between them without casts.
 */

export type Route =
  | { kind: "panel"; panel: "dashboard" | "catalogue" | "connect" | "clients" | "chase" | "adoption" }
  | { kind: "client"; eocId: string; tab: "developmental" | "tasks" | "dailylog" | "narrative" }
  | { kind: "connect-client"; eocId: string }
  | { kind: "admin"; panel: "users-roles" | "audit-log" | "access-requests" };

const DETAIL_TABS = ["developmental", "tasks", "dailylog", "narrative"];

/** Parse a `location.hash` string into a Route. Unknown hashes → dashboard. */
export function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const [p0, p1, p2] = parts;

  if (!p0 || p0 === "dashboard") return { kind: "panel", panel: "dashboard" };

  if (p0 === "clients") {
    if (p1) {
      const tab = (DETAIL_TABS.includes(p2) ? p2 : "developmental") as "developmental" | "tasks" | "dailylog" | "narrative";
      return { kind: "client", eocId: p1, tab };
    }
    return { kind: "panel", panel: "clients" };
  }

  if (p0 === "admin") {
    return {
      kind: "admin",
      panel: p1 === "audit-log"
        ? "audit-log"
        : p1 === "access-requests"
          ? "access-requests"
          : "users-roles",
    };
  }

  if (p0 === "connect") {
    // #/connect → overview ; #/connect/<eocId> → a client's sensor timeline
    // (OMON-253: refresh-safe deep link, mirroring #/clients/<eocId>).
    if (p1) return { kind: "connect-client", eocId: p1 };
    return { kind: "panel", panel: "connect" };
  }

  if (p0 === "catalogue") return { kind: "panel", panel: "catalogue" };

  // MONO-111 phase 2b: the office chase queue (admin/planner).
  if (p0 === "chase") return { kind: "panel", panel: "chase" };

  // Soma planner adoption (admin-only; Ed, 2026-09-22).
  if (p0 === "adoption") return { kind: "panel", panel: "adoption" };

  return { kind: "panel", panel: "dashboard" }; // unknown → landing
}

/** Build the `location.hash` string for a Route (inverse of parseHash). */
export function buildHash(route: Route): string {
  switch (route.kind) {
    case "client": return `#/clients/${route.eocId}/${route.tab}`;
    case "connect-client": return `#/connect/${route.eocId}`;
    case "admin": return `#/admin/${route.panel}`;
    case "panel": return `#/${route.panel}`;
  }
}
